import React, { useState, useEffect } from 'react';
import { X, Shield, Trash2 } from 'lucide-react';
import * as imageService from '../services/imageService';
import UserSearchInput from './UserSearchInput';

const ImageSharingModal = ({ image, onClose }) => {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchGrants();
  }, []);

  const fetchGrants = async () => {
    setLoading(true);
    try {
      const data = await imageService.getImageGrants(image.id);
      if (data.success) setGrants(data.grants || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (user) => {
    setError(null);
    try {
      const data = await imageService.addImageGrant(image.id, user.id);
      if (data.success) setGrants(data.grants || []);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveUser = async (userId) => {
    setError(null);
    try {
      await imageService.removeImageGrant(image.id, userId);
      setGrants(grants.filter(g => g.user_id !== userId));
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[70vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex justify-between items-center rounded-t-lg">
          <div className="flex items-center space-x-3 text-white">
            <Shield size={20} />
            <h2 className="text-lg font-bold">Share — {image.name}</h2>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Add Users</label>
            <UserSearchInput onSelectUser={handleAddUser} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Granted Users ({grants.length})
            </label>
            {loading ? (
              <div className="text-center py-4 text-gray-500 text-sm">Loading...</div>
            ) : grants.length > 0 ? (
              <div className="space-y-2">
                {grants.map(grant => (
                  <div key={grant.user_id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <div>
                      <span className="text-sm font-medium">{grant.first_name || grant.username || 'User'} {grant.last_name || ''}</span>
                      <span className="text-xs text-gray-500 ml-2">{grant.email}</span>
                    </div>
                    <button
                      onClick={() => handleRemoveUser(grant.user_id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500 text-sm">
                No users granted access yet.
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors text-sm"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageSharingModal;
